import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

export function emptyPacienteForm() {
  return {
    id: null,
    nombre: "",
    apellido: "",
    dni: "",
    telefono: "",
    fecha: "",
    lugar: "",
    antecedentes: "",
    medicacion: "",
    enfermedadSistemica: "",
    diagnostico: "",
    tratamiento: "",
    observaciones: "",
    obraSocial: "",
    prestaciones: [],
  };
}

function mapApiPacienteToForm(p) {
  if (!p) return emptyPacienteForm();
  return {
    ...emptyPacienteForm(),
    id: p.id,
    nombre: p.nombre ?? "",
    apellido: p.apellido ?? "",
    dni: p.dni ?? "",
    telefono: p.telefono ?? "",
    fecha: p.fecha ?? "",
    medicacion: p.medicacion ?? "",
    enfermedadSistemica: p.detalleCondicion ?? "",
    diagnostico: p.diagnostico ?? "",
    tratamiento: p.plan ?? "",
    observaciones: p.observaciones ?? "",
    obraSocial: p.obraSocial ?? "",
  };
}

/**
 * Modal alta/edición de paciente (mismo markup que PacientesPage).
 * @param {boolean} open
 * @param {object|null} paciente — null = nuevo
 * @param {() => void} onClose
 * @param {(saved?: object) => void} [onSaved]
 */
export default function PacienteFormModal({ open, paciente, onClose, onSaved }) {
  const navigate = useNavigate();
  const [form, setForm] = useState(emptyPacienteForm());
  const isEdit = Boolean(paciente?.id);

  useEffect(() => {
    if (!open) return;
    setForm(isEdit ? mapApiPacienteToForm(paciente) : emptyPacienteForm());
  }, [open, paciente, isEdit]);

  const handleChange = (e) => {
    if (e.target.name === "prestaciones") {
      setForm({
        ...form,
        prestaciones: e.target.value.split(",").map((x) => x.trim()).filter(Boolean),
      });
    } else {
      setForm({ ...form, [e.target.name]: e.target.value });
    }
  };

  const handleSave = async () => {
    if (!form.nombre.trim() || !form.apellido.trim() || !form.dni.trim()) {
      alert("Completá nombre, apellido y DNI.");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/login");
        return;
      }

      const datosParaBackend = {
        nombre: form.nombre,
        apellido: form.apellido,
        dni: form.dni,
        telefono: form.telefono,
        fecha: form.fecha,
        medicacion: form.medicacion,
        diagnostico: form.diagnostico,
        observaciones: form.observaciones,
        plan: form.tratamiento,
        condicion: form.enfermedadSistemica ? "si" : "no",
        detalleCondicion: form.enfermedadSistemica,
        tomaMedicacion: form.medicacion ? "si" : "no",
      };

      const url = isEdit
        ? `http://127.0.0.1:8000/api/pacientes/${form.id}`
        : `http://127.0.0.1:8000/api/pacientes`;
      const method = isEdit ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(datosParaBackend),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Error al guardar");
      }

      const json = await response.json();
      onClose();
      if (typeof onSaved === "function") onSaved(json.data);
    } catch (error) {
      console.error(error);
      alert(error.message);
    }
  };

  if (!open) return null;

  return (
    <div className="modal-historia" role="presentation" onClick={onClose}>
      <div
        className="modal-historia-content"
        role="dialog"
        aria-modal="true"
        aria-labelledby="paciente-form-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="paciente-form-modal-title">{isEdit ? "Editar Paciente" : "Nuevo Paciente"}</h2>

        <div className="modal-historia-grid">
          <input name="nombre" placeholder="Nombre (*)" value={form.nombre} onChange={handleChange} />
          <input name="apellido" placeholder="Apellido (*)" value={form.apellido} onChange={handleChange} />
          <input name="dni" placeholder="DNI (*)" value={form.dni} onChange={handleChange} required />
          <input name="telefono" placeholder="Teléfono" value={form.telefono} onChange={handleChange} />
          <input name="fecha" placeholder="Fecha Nacimiento" value={form.fecha} onChange={handleChange} />
          <input name="lugar" placeholder="Lugar" value={form.lugar} onChange={handleChange} />
          <input name="antecedentes" placeholder="Antecedentes" value={form.antecedentes} onChange={handleChange} />
          <input name="medicacion" placeholder="Medicación" value={form.medicacion} onChange={handleChange} />
          <input name="enfermedadSistemica" placeholder="Enf. Sistémica" value={form.enfermedadSistemica} onChange={handleChange} />
          <input name="diagnostico" placeholder="Diagnóstico" value={form.diagnostico} onChange={handleChange} />
          <input name="tratamiento" placeholder="Plan / Tratamiento" value={form.tratamiento} onChange={handleChange} />
          <input name="obraSocial" placeholder="Obra Social" value={form.obraSocial} onChange={handleChange} />
        </div>

        <label>Observaciones:</label>
        <textarea
          name="observaciones"
          placeholder="Observaciones generales..."
          value={form.observaciones}
          onChange={handleChange}
        />

        <div className="modal-historia-buttons">
          <button type="button" onClick={onClose} className="btn-cerrar">
            Cancelar
          </button>
          <button type="button" onClick={handleSave} className="btn-guardar">
            {isEdit ? "Guardar Cambios" : "Guardar Paciente"}
          </button>
        </div>
      </div>
    </div>
  );
}
